import React, { useState, useEffect, useCallback } from "react";
import { Badge, Button } from "reactstrap";
import FiscalYearService from "../../../services/AccountingServices/FiscalYearService";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import useTableSearch from "../../../hooks/useTableSearch";
import FiscalYearForm from "./FiscalYearForm";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";

const FiscalYears = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await FiscalYearService.getList();
      console.log("🚀 ~ FiscalYears ~ response:", response);
      setData(Array.isArray(response) ? response : response?.items || []);
    } catch (error) {
      console.error("Error fetching fiscal years:", error);
      toast.error("Failed to fetch fiscal years");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { searchTerm, setSearchTerm, filteredData } = useTableSearch(data, ["name", "code"]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const toggleForm = () => {
    if (isFormOpen) setSelectedItem(null);
    setIsFormOpen(!isFormOpen);
  };

  const toggleDelete = () => setIsDeleteOpen(!isDeleteOpen);

  const handleAdd = () => {
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await FiscalYearService.delete(selectedItem.code);
      toast.success("Fiscal Year deleted successfully");
      fetchData();
      toggleDelete();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete fiscal year");
    }
  };

  const handleActivate = async (code) => {
    try {
      await FiscalYearService.activate(code);
      toast.success("Fiscal Year activated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to activate fiscal year");
    }
  };

  const columns = [
    {
      name: "Code",
      selector: (row) => row.code,
      sortable: true,
      width: "120px",
      cell: (row) => <span className="fs-12">{row.code}</span>,
    },
    {
      name: "Fiscal Year Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => <span className="fw-semibold text-primary">{row.name}</span>,
    },
    {
      name: "Start Date",
      selector: (row) => `${row.startDate}`,
      sortable: false,
      cell: (row) => (
        <div className="text-muted small">
          <div><i className="ri-calendar-line me-1"></i> {new Date(row.startDate).toLocaleDateString()}</div>
        </div>
      ),
    },
    {
      name: "End Date",
      selector: (row) => `${row.endDate}`,
      sortable: false,
      cell: (row) => (
        <div className="text-muted small">
          <div><i className="ri-calendar-line me-1"></i> {new Date(row.endDate).toLocaleDateString()}</div>
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.isActive,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <Badge color={row.isActive ? "success" : "danger"} pill className="text-uppercase">
          {row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  const stats = [
    {
      title: "Total Fiscal Years",
      value: data.length,
      color: "#4B38B3",
      icon: "bx bx-calendar",
    },
    {
      title: "Active Year",
      value: data.find((y) => y.isActive)?.name || "None",
      color: "#0AB39C",
      icon: "bx bx-check-double",
    },
  ];

  return (
    <div className="page-content p-0">
      <ResponsiveDataManagement
        title="Fiscal Years"
        pageTitle="Accounting Setup"
        mobileCardTitle="Fiscal Year List"
        data={filteredData}
        columns={columns}
        stats={stats}
        searchKeys={["name", "code"]}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        loading={loading}
        totalRows={filteredData.length}
        onSearch={setSearchTerm}
        searchTerm={searchTerm}
        emptyStateMessage="No fiscal years found"
        customActions={[
          {
            label: "Activate",
            icon: "bx bx-history",
            color: "soft-info",
            onClick: (row) => handleActivate(row.code),
          },
        ]}
        mobileCardRenderer={(item) => (
          <div key={item.code} className="card-box shadow-sm mb-3 border rounded p-3">
             <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h6 className="fw-bold text-primary mb-1">{item.name}</h6>
                  <Badge color="soft-dark" size="sm">{item.code}</Badge>
                </div>
                <Badge color={item.isActive ? "success" : "light"} pill>
                   {item.isActive ? "Active" : "Inactive"}
                </Badge>
             </div>
             <div className="text-muted small mb-3">
                <div><i className="ri-calendar-line me-1"></i> {new Date(item.startDate).toLocaleDateString()} - {new Date(item.endDate).toLocaleDateString()}</div>
             </div>
             <div className="d-flex gap-2 justify-content-end border-top pt-2">
                {!item.isActive && (
                  <Button color="success" size="sm" onClick={() => handleActivate(item.code)}>Activate</Button>
                )}
                <Button color="info" size="sm" onClick={() => handleEdit(item)}>Edit</Button>
                <Button color="danger" size="sm" onClick={() => handleDeleteClick(item)}>Delete</Button>
             </div>
          </div>
        )}
      />

      <FiscalYearForm
        isOpen={isFormOpen}
        toggle={toggleForm}
        selectedItem={selectedItem}
        refetch={fetchData}
      />

      <DeleteModal
        show={isDeleteOpen}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDeleteConfirm}
        itemName={selectedItem?.name}
      />
    </div>
  );
};

export default FiscalYears;
